import axios from "../config/connections"
import { sendCaptureExceptionToSentry } from "../utils/sentry"
import config from "../config"
import { IFeature, IGeometry } from "interfaces"

const FEATURES = [
  "venue",
  "unit",
  "section",
  "fixture",
  "level",
  "amenity",
  "taxonomy",
  "anchor",
  "occupant",
  "kiosk",
  "opening",
  "relationship",
  "privilege",
  "event",
  "promotion",
  "footprint",
]

interface IFavIcon {
  size: string
  src: string
  type: string
}

interface IMenuConfig {
  to: string
  label: string
  order?: number
  icon_comp?: string
  icon_path?: string
  image?: string
  hidden?: boolean
}

interface IMapExtrudeConfig {
  height: number
  category: string
}

interface IMapLightConfig {
  color: string
  position?: number[]
  intensity: number
}

interface IMapConfig {
  default_zoom: number
  extrude: {
    kiosk: IMapExtrudeConfig[]
    unit: IMapExtrudeConfig[]
  }
  light: {
    ambientLight: IMapLightConfig
    directionalLight: IMapLightConfig
  }
}

interface IDeviceConfig {
  navigation: {
    main: IMenuConfig[]
    menus: IMenuConfig[]
  }
  search: {
    featureTypes: string[]
    key: {
      name: string
      weight: number
    }
  }
  autoplay?: {
    step: {
      delay: number
      enable: boolean
    }
  }
}

interface IAppConfig {
  homeNavigations: IMenuConfig[]
  kiosk: IDeviceConfig
  kioskNavigations: IMenuConfig[]
  map: IMapConfig
  mobile: IDeviceConfig
}

interface IAppManifest {
  icons: IFavIcon[]
  name: string
  theme_color?: string
}

interface IGeojson {
  type: string
  features: IFeature[]
}

type Coordinate = [number, number]
type LinearRingCoordinates = Coordinate[]
type PolygonCoordinates = LinearRingCoordinates[]
type MultiPolygonCoordinates = PolygonCoordinates[]

interface IMapLabel {
  name: string
  geometry: {
    coordinates: PolygonCoordinates | MultiPolygonCoordinates
  }
  properties: Record<string, any>
}

interface IMapDecoration {
  name: string
  geometry: IGeometry
}

type FetchGeojson = (type: string) => Promise<{
  type: string
  data: IFeature[]
}>

type FetchImdfGeojson = () => Promise<{
  features: IFeature[]
  [k: string]: IFeature[]
}>

export const pluralize = (word: string): string => {
  if (word.substr(-1, 1) === "y") return word.replace(/y$/, "ies")
  return `${word}s`
}

const get = async <T>(endpoint: string): Promise<T> => {
  try {
    const res = await axios.get(endpoint)
    if (res.status !== 200) {
      throw new Error()
    }
    return res.data as T
  } catch (err) {
    throw err.message
  }
}

export const fetchAppManifest = async () => {
  try {
    const path = `/manifest.json`
    const jsonRes = await get<IAppManifest>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /manifest.json endpoint.`,
    })

    // Return empty object if error
    return {}
  }
}

export const fetchGeojson: FetchGeojson = async (type) => {
  try {
    const path = `/imdf/${type}.geojson`
    const jsonRes = await get<IGeojson>(path)
    return { type, data: jsonRes.features }
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /imdf/${type}.geojson endpoint.`,
    })

    // Return empty array if error
    return { type, data: [] }
  }
}

const fetchAllGeojson = () => Promise.allSettled(FEATURES.map(fetchGeojson))

export const fetchImdfGeojson: FetchImdfGeojson = async () => {
  try {
    const geojsonResult = await fetchAllGeojson()
    return geojsonResult.reduce(
      (acc, feature) => {
        if (feature.status === "fulfilled") {
          const { type, data } = feature.value
          return {
            ...acc,
            [pluralize(type)]: data,
            features: [...acc.features, ...data],
          }
        }
        return acc
      },
      { features: [] }
    )
  } catch (err) {
    throw err
  }
}

export const fetchMapLabels = async () => {
  const path = `/labels.geojson`
  try {
    const jsonRes = await get<IMapLabel[]>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })
    // Return empty array if error
    return { data: [] }
  }
}

export const fetchAppConfig = async () => {
  try {
    const path = `${config("host")}/delivery/config/${config("appConfigId")}`
    const jsonRes = await get<IAppConfig>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch App config endpoint.`,
    })

    // Return null if error
    return null
  }
}

export const fetchMapDecorations = async () => {
  const path = `/elements`
  try {
    const jsonResponse = await get<IMapDecoration[]>(path)
    return jsonResponse
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })
    return { data: [] }
  }
}
